import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  Star, 
  Crown, 
  Sparkles,
  TrendingUp,
  Settings
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import PackageRequestModal from './PackageRequestModal';

const PackagesPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  
  const isArabic = i18n.language === 'ar';

  const packages = [
    {
      id: 'basic',
      nameAr: 'وازن بيسك',
      nameEn: 'Wazin Basic',
      icon: Zap,
      taglineAr: 'بداية ذكية لعلاقة قانونية آمنة',
      taglineEn: 'Smart start for a secure legal relationship',
      color: 'from-wazin-navy to-wazin-navy-700',
      targetAudienceAr: 'أصحاب المشاريع الناشئة، المتاجر الإلكترونية الصغيرة، وروّاد الأعمال في بداية الطريق',
      targetAudienceEn: 'Startups, small e-commerce stores, and entrepreneurs at the beginning',
      priceMonthly: '2,500 - 4,000',
      priceYearly: '25,000 - 40,000',
      featuresAr: [
        '3 استشارات قانونية شهرية',
        'مراجعة عقد واحد شهرياً',
        'خطاب قانوني واحد شهرياً',
        'خصم 10٪ على أتعاب القضايا',
        'حساب في منصة وازن مع لوحة متابعة',
        'دعم عبر نظام التذاكر (أولوية عادية)'
      ],
      featuresEn: [
        '3 legal consultations per month',
        'Review one contract per month',
        'One legal letter per month',
        '10% discount on litigation fees',
        'Wazin platform account with dashboard',
        'Support via ticket system (normal priority)'
      ]
    },
    {
      id: 'plus',
      nameAr: 'وازن بلس',
      nameEn: 'Wazin Plus',
      icon: TrendingUp,
      taglineAr: 'أكثر من استشارة… شراكة قانونية مستمرة مع نشاطك',
      taglineEn: 'More than consultation... an ongoing legal partnership with your business',
      color: 'from-wazin-gold-600 to-wazin-gold-700',
      targetAudienceAr: 'المؤسسات الصغيرة والمتوسطة مع تعاقدات مستمرة',
      targetAudienceEn: 'Small and medium enterprises with ongoing contracts',
      priceMonthly: '5,000 - 8,000',
      priceYearly: '55,000 - 85,000',
      popular: true,
      featuresAr: [
        '6 استشارات شهرية (هاتفية/مرئية/حضور)',
        'مراجعة حتى 3 عقود شهرياً',
        'إعداد حتى خطابين قانونيين شهرياً',
        'مراجعة بسيطة للسياسات الداخلية',
        'خصم 20٪ على أتعاب القضايا',
        '3 تذاكر دعم مفتوحة متزامنة',
        'تقرير قانوني ربع سنوي'
      ],
      featuresEn: [
        '6 monthly consultations (phone/video/in-person)',
        'Review up to 3 contracts monthly',
        'Prepare up to 2 legal letters monthly',
        'Simple review of internal policies',
        '20% discount on litigation fees',
        '3 concurrent open support tickets',
        'Quarterly legal report'
      ]
    },
    {
      id: 'premium',
      nameAr: 'وازن بريميوم',
      nameEn: 'Wazin Premium',
      icon: Star,
      taglineAr: 'باقة الشركات النشطة… كل ما تحتاجه لضبط عقودك وموظفيك وقضاياك',
      taglineEn: 'Active companies package... everything you need for contracts, employees and cases',
      color: 'from-wazin-navy-500 to-wazin-navy-600',
      targetAudienceAr: 'الشركات المتوسطة بحركة عقود مستمرة',
      targetAudienceEn: 'Medium-sized companies with continuous contract activity',
      priceMonthly: '10,000 - 15,000',
      priceYearly: '110,000 - 160,000',
      featuresAr: [
        'استشارات شبه غير محدودة (سياسة استخدام عادل)',
        'مراجعة 6 عقود + صياغة عقدين شهرياً',
        'دعم قانوني لقسم الموارد البشرية',
        'حضور اجتماع قانوني كل أسبوعين',
        'خصم 30٪ على أتعاب القضايا',
        'مراجعة دورية لعقود الموردين والعملاء',
        'تقرير قانوني شهري للإدارة',
        'أولوية أعلى في الدعم والتذاكر'
      ],
      featuresEn: [
        'Nearly unlimited consultations (fair use policy)',
        'Review 6 contracts + draft 2 contracts monthly',
        'Legal support for HR department',
        'Legal meeting attendance every two weeks',
        '30% discount on litigation fees',
        'Periodic review of supplier and client contracts',
        'Monthly legal report for management',
        'Higher priority in support and tickets'
      ]
    },
    {
      id: 'platinum',
      nameAr: 'وازن بلاتينيوم',
      nameEn: 'Wazin Platinum',
      icon: Crown,
      taglineAr: 'فريق قانوني شبه متفرغ لإدارتك… بدون تكلفة توظيف داخلية',
      taglineEn: 'Semi-dedicated legal team for your management... without internal hiring costs',
      color: 'from-wazin-gold to-wazin-gold-600',
      targetAudienceAr: 'الشركات الكبيرة والمجموعات',
      targetAudienceEn: 'Large companies and groups',
      priceMonthly: '20,000 - 35,000',
      priceYearly: '220,000 - 380,000',
      featuresAr: [
        'استشارات غير محدودة (سياسة استخدام عادل)',
        'صياغة 5 عقود + مراجعة 10 عقود شهرياً',
        'إعداد أو تحديث اللائحة الداخلية',
        'محامٍ مسؤول (Account Manager) مخصص',
        'حضور أسبوعي في المقر أو أونلاين',
        'إدارة القضايا الأساسية',
        'تقارير شهرية مفصلة للإدارة العليا',
        'إعداد نماذج ثابتة للخطابات القانونية'
      ],
      featuresEn: [
        'Unlimited consultations (fair use policy)',
        'Draft 5 contracts + review 10 contracts monthly',
        'Prepare or update internal regulations',
        'Dedicated Account Manager lawyer',
        'Weekly attendance at office or online',
        'Management of basic cases',
        'Detailed monthly reports for senior management',
        'Prepare standard templates for legal letters'
      ]
    },
    {
      id: 'elite',
      nameAr: 'وازن إيليت',
      nameEn: 'Wazin Elite',
      icon: Sparkles,
      taglineAr: 'إدارة قانونية كاملة كخدمة… وازن تصبح الذراع القانوني الاستراتيجي لمنظمتك',
      taglineEn: 'Complete legal management as a service... Wazin becomes your strategic legal arm',
      color: 'from-wazin-navy-600 to-wazin-navy-800',
      targetAudienceAr: 'الشركات الكبرى والمجموعات',
      targetAudienceEn: 'Major corporations and groups',
      priceMonthly: '50,000 - 120,000',
      priceYearly: '600,000 - 1,200,000',
      premium: true,
      featuresAr: [
        'فريق قانوني كامل مخصص للمنشأة',
        'مدير حساب قانوني (Legal Account Director)',
        'تداول غير محدود في العقود',
        'إدارة جميع القضايا الجديدة والقائمة',
        'حضور اجتماعات مجلس الإدارة',
        'تصميم وتنفيذ منظومة حوكمة كاملة',
        'تقارير دورية (شهرية/أسبوعية)',
        'خط دعم VIP (واتساب/إيميل)',
        'أولوية قصوى في جميع الخدمات'
      ],
      featuresEn: [
        'Complete dedicated legal team for the organization',
        'Legal Account Director',
        'Unlimited contracts handling',
        'Management of all new and existing cases',
        'Board of Directors meeting attendance',
        'Design and implement complete governance system',
        'Periodic reports (monthly/weekly)',
        'VIP support line (WhatsApp/Email)',
        'Highest priority in all services'
      ]
    },
    {
      id: 'custom',
      nameAr: 'وازن كستم',
      nameEn: 'Wazin Custom',
      icon: Settings,
      taglineAr: 'لا تناسبك الباقات الجاهزة؟ صمّم باقتك على مقاس نشاطك',
      taglineEn: "Don't fit standard packages? Design your package tailored to your business",
      color: 'from-wazin-gold to-wazin-gold-600',
      targetAudienceAr: 'جميع الفئات مع احتياجات مخصصة',
      targetAudienceEn: 'All categories with custom needs',
      priceMonthly: 'حسب الطلب',
      priceYearly: 'حسب الطلب',
      custom: true,
      featuresAr: [
        'اختر عدد الاستشارات الشهرية',
        'حدد عدد العقود للمراجعة والصياغة',
        'اختر دعم الموارد البشرية',
        'حدد عدد القضايا المتوقعة',
        'اختر حضور المحامي (عدد المرات)',
        'خدمات حوكمة وامتثال حسب الحاجة',
        'ملكية فكرية وامتياز تجاري',
        'سعر مخصص وشفاف حسب احتياجك'
      ],
      featuresEn: [
        'Choose number of monthly consultations',
        'Define number of contracts for review and drafting',
        'Choose HR support',
        'Define expected number of cases',
        'Choose lawyer attendance (number of times)',
        'Governance and compliance services as needed',
        'Intellectual property and franchise',
        'Custom and transparent pricing based on your needs'
      ]
    }
  ];

  const handleRequestPackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowRequestModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-wazin-gold-50 to-wazin-navy-50" dir="rtl">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-wazin-navy to-wazin-navy-dark text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center text-white">
            {isArabic ? 'الباقات القانونية السنوية' : 'Annual Legal Packages'}
          </h1>
          <p className="text-xl text-center text-wazin-gold-100 mb-2 max-w-4xl mx-auto">
            {isArabic ? 'اختر شريكك القانوني لا مجرد محامٍ' : 'Choose your legal partner, not just a lawyer'}
          </p>
          <p className="text-center text-wazin-gold-200 max-w-4xl mx-auto">
            {isArabic 
              ? 'باقات قانونية متدرجة تغطي احتياج الأفراد وقطاع الأعمال، من البداية البسيطة إلى الشراكة الاستراتيجية الكاملة'
              : 'Tiered legal packages covering the needs of individuals and businesses, from simple beginnings to complete strategic partnerships'}
          </p>
          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/packages/compare')}
              className="bg-wazin-gold text-wazin-navy px-8 py-3 rounded-xl font-bold hover:bg-wazin-gold-light transition-all shadow-lg"
            >
              {isArabic ? 'قارن بين الباقات' : 'Compare Packages'}
            </button>
          </div>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border border-wazin-gold-200 ${
                pkg.popular || pkg.premium ? 'ring-4 ring-wazin-gold transform scale-105' : ''
              }`}
            >
              {/* Package Header */}
              <div className={`bg-gradient-to-r ${pkg.color} ${pkg.color.includes('gold') ? 'text-wazin-navy' : 'text-white'} p-6 relative`}>
                {pkg.popular && (
                  <div className="absolute top-4 left-4 bg-wazin-gold text-wazin-navy px-3 py-1 rounded-full text-xs font-bold">
                    {isArabic ? 'الأكثر طلباً' : 'Most Popular'}
                  </div>
                )}
                {pkg.premium && (
                  <div className="absolute top-4 left-4 bg-wazin-gold text-wazin-navy px-3 py-1 rounded-full text-xs font-bold">
                    {isArabic ? 'حصري' : 'Exclusive'}
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-xl">
                    <pkg.icon size={32} className={pkg.color.includes('gold') ? 'text-wazin-navy' : 'text-white'} />
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold ${pkg.color.includes('gold') ? 'text-wazin-navy' : 'text-white'}`}>
                      {isArabic ? pkg.nameAr : pkg.nameEn}
                    </h3>
                    <p className={`text-sm opacity-90 ${pkg.color.includes('gold') ? 'text-wazin-navy' : 'text-white'}`}>
                      {isArabic ? pkg.nameEn : pkg.nameAr}
                    </p>
                  </div>
                </div>
                <p className={`text-sm leading-relaxed opacity-95 ${pkg.color.includes('gold') ? 'text-wazin-navy' : 'text-white'}`}>
                  {isArabic ? (pkg.taglineAr || pkg.tagline) : (pkg.taglineEn || pkg.tagline)}
                </p>
              </div>

              {/* Package Body */}
              <div className="p-6">
                {/* Target Audience */}
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2 font-semibold">
                    {isArabic ? 'مناسبة لـ:' : 'Suitable for:'}
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {isArabic ? (pkg.targetAudienceAr || pkg.targetAudience) : (pkg.targetAudienceEn || pkg.targetAudience)}
                  </p>
                </div>

                {/* Price */}
                <div className="mb-6 p-4 bg-wazin-gold-50 rounded-xl border border-wazin-gold-200">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-wazin-navy">{pkg.priceMonthly}</span>
                    <span className="text-wazin-navy-600">{isArabic ? 'ريال/شهرياً' : 'SAR/month'}</span>
                  </div>
                  {pkg.priceYearly !== 'حسب الطلب' && pkg.priceYearly !== 'On Request' && (
                    <p className="text-sm text-wazin-navy-500 mt-1">
                      {isArabic ? `أو ${pkg.priceYearly} ريال سنوياً` : `or ${pkg.priceYearly} SAR/year`}
                    </p>
                  )}
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  {((isArabic ? pkg.featuresAr : pkg.featuresEn) || pkg.featuresAr || pkg.features || []).map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="text-wazin-gold flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-wazin-navy-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={() => handleRequestPackage(pkg)}
                    className={`w-full bg-gradient-to-r ${pkg.color} text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2`}
                  >
                    {pkg.custom 
                      ? (isArabic ? 'صمّم باقتك' : 'Customize Your Package')
                      : (isArabic ? 'اطلب الباقة' : 'Request Package')}
                    <ArrowRight size={20} className={isArabic ? 'rotate-180' : ''} />
                  </button>
                  <button
                    onClick={() => navigate('/packages/compare')}
                    className="w-full border-2 border-wazin-gold text-wazin-navy py-3 rounded-xl font-semibold hover:bg-wazin-gold hover:text-white transition-all"
                  >
                    {isArabic ? 'قارن الباقات' : 'Compare Packages'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-wazin-navy py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-wazin-gold">
            مميزات الاشتراك في الباقات القانونية
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-gradient-to-br from-wazin-gold to-wazin-gold-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">فريق قانوني متفرغ لك</h3>
              <p className="text-wazin-gold-100">
                محامون ومستشارون متخصصون يتابعون احتياجاتك القانونية بشكل مستمر
              </p>
            </div>
            <div className="text-center">
              <div className="bg-gradient-to-br from-wazin-gold to-wazin-gold-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">منصة رقمية متكاملة</h3>
              <p className="text-wazin-gold-100">
                تابع قضاياك وعقودك واستشاراتك عبر منصة وازن الرقمية و Wazin AI
              </p>
            </div>
            <div className="text-center">
              <div className="bg-gradient-to-br from-wazin-gold to-wazin-gold-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">تكلفة ثابتة ومتوقعة</h3>
              <p className="text-wazin-gold-100">
                لا مفاجآت في الفواتير، اشتراك شهري أو سنوي بسعر محدد مسبقاً
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-wazin-navy to-wazin-navy-dark text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            هل تحتاج استشارة قبل اختيار الباقة؟
          </h2>
          <p className="text-xl text-wazin-gold-100 mb-8">
            تواصل معنا وسنساعدك في اختيار الباقة الأنسب لاحتياجك
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="bg-wazin-gold text-wazin-navy px-8 py-4 rounded-xl font-bold text-lg hover:bg-wazin-gold-light transition-all shadow-lg hover:shadow-xl"
          >
            تواصل معنا الآن
          </button>
        </div>
      </div>

      {/* Package Request Modal */}
      {showRequestModal && (
        <PackageRequestModal
          package={selectedPackage}
          onClose={() => {
            setShowRequestModal(false);
            setSelectedPackage(null);
          }}
        />
      )}
    </div>
  );
};

export default PackagesPage;
